import DashboardPreview from 'features/Admin/Roles/RoleManager/components/RoleTabs/components/DashboardPreview';
import WidgetSelector from 'features/dashboard/features/WidgetSelector';
import type { LayoutItem, WidgetData } from 'features/dashboard/types/widgetTypes';
import React, { useEffect, useRef, useState } from 'react';
import { PermItem, RoleFormData } from '../../../types/Roles.types';

interface DashboardEditTabProps {
  formData: RoleFormData;
  setFormData: React.Dispatch<React.SetStateAction<RoleFormData>>;
  checks?: Record<string, boolean>;
  widgetData?: WidgetData;
  permList?: PermItem[];
  onNext?: () => void;
  compactPreview?: boolean;
}

const DashboardEditTab: React.FC<DashboardEditTabProps> = ({
  formData,
  setFormData,
  checks,
  widgetData,
  permList,
  onNext,
  compactPreview = false,
}) => {
  const [widgetSelectorOpen, _setWidgetSelectorOpen] = useState(false);
  const [previewLayout, setPreviewLayout] = useState<LayoutItem[]>(formData.layout || []);
  const previewLayoutRef = useRef(previewLayout);

  const setWidgetSelectorOpen = (open: boolean) => {
    _setWidgetSelectorOpen(open);
  };

  const normalizePermName = (permName: string) =>
    permName.replace(/\s/g, '').replace(/\//g, '-').replace(/:/g, '_');

  const hasConnectionPermission = (regex: RegExp) =>
    (permList || []).some(
      (perm, index) => regex.test(normalizePermName(perm.name)) && formData.permissions[index],
    );

  const checkWidgetPermission = (permName: string) => {
    switch (permName) {
      case 'ViewConnections':
        return hasConnectionPermission(/^View[A-Za-z]+Connections$/);
      case 'Create-EditConnections':
        return hasConnectionPermission(/^Create-Edit[A-Za-z]+Connections$/);
      case 'DeleteConnections':
        return hasConnectionPermission(/^Delete[A-Za-z]+Connections$/);
      case 'ImportConnections':
        return hasConnectionPermission(/^Import[A-Za-z]+Connections$/);
      default: {
        const index = (permList || []).findIndex(
          (perm) => normalizePermName(perm.name) === permName,
        );
        return index >= 0 ? !!formData.permissions[index] : false;
      }
    }
  };

  useEffect(() => {
    setPreviewLayout(formData.layout || []);
  }, [formData.layout]);

  useEffect(() => {
    previewLayoutRef.current = previewLayout;
  }, [previewLayout]);

  useEffect(() => {
    return () => {
      setFormData((prev: RoleFormData) => {
        if (prev.layout === previewLayoutRef.current) return prev;
        return { ...prev, layout: previewLayoutRef.current };
      });
    };
  }, [setFormData]);

  const addWidgets = (widgets: LayoutItem[]) => {
    if (!Array.isArray(widgets) || !widgets.length) return;
    setPreviewLayout((prev) => [...prev, ...widgets]);
  };

  const removeWidget = (index: number) => {
    setPreviewLayout((prev) => prev.filter((_, i) => i !== index));
  };

  const onListChange = (newList: LayoutItem[]) => {
    setPreviewLayout([...newList]);
  };

  return (
    <div className="d-flex flex-column w-100 h-100 user-select-none" style={{ minHeight: 0 }}>
      <div className="d-flex flex-row flex-grow-1 min-h-0 overflow-hidden border border-1 border-white bg-background position-relative">
        <DashboardPreview
          definedWidgetData={widgetData}
          list={previewLayout}
          setList={onListChange}
          onRemoveWidget={removeWidget}
          openWidgetSelector={() => setWidgetSelectorOpen(true)}
          uninteractable={false}
          compactPreview={compactPreview}
        />
      </div>
      {widgetData && widgetSelectorOpen && (
        <WidgetSelector
          show={widgetSelectorOpen}
          handleShow={() => setWidgetSelectorOpen(false)}
          widgetData={widgetData}
          userList={Object.keys(widgetData)
            .filter((widgetId) =>
              (widgetData[widgetId].permissions || []).every((permName: string) =>
                checkWidgetPermission(permName),
              ),
            )
            .map((widgetId) => {
              return widgetData[widgetId];
            })}
          onAddWidgets={addWidgets}
          onAddWidget={(w: LayoutItem) => addWidgets([w])}
        />
      )}
    </div>
  );
};

export default DashboardEditTab;
